import React, { useMemo } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import type { Moment } from 'moment';
import { Clickable } from '../common/Clickable';
import { SvgIcon } from '../common/SvgIcon';
import { appTheme, useThemeColors } from '../../shared/theme';
import type { ThemeColors } from '../../shared/theme/store';
import { formatDisplayMonthYear } from '../../shared/i18n';
import { horizontalLtrDirectionStyle } from '../../shared/i18n/rtl';

export type CalendarMonthNavRowProps = {
  currentMonth: Moment;
  canNavigatePrev: boolean;
  canNavigateNext: boolean;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  previousMonthLabel: string;
  nextMonthLabel: string;
  chevronActiveColor?: string;
  chevronDisabledColor?: string;
};

/**
 * Shared month title + prev/next chevrons for delivery-dates / freeze calendar modals.
 * Row is LTR so prev stays physical-left (matches LTR calendar grids).
 */
export function CalendarMonthNavRow({
  currentMonth,
  canNavigatePrev,
  canNavigateNext,
  onPrevMonth,
  onNextMonth,
  previousMonthLabel,
  nextMonthLabel,
  chevronActiveColor,
  chevronDisabledColor,
}: CalendarMonthNavRowProps) {
  const c = useThemeColors();
  const row_style = useMemo(() => create_month_row_style(c), [c]);
  const month_title_style = useMemo(() => [styles.monthTitle, { color: c.text }], [c]);
  const active = chevronActiveColor ?? c.text;
  const disabled = chevronDisabledColor ?? c.border;
  return (
    <View style={row_style}>
      <Clickable onPress={onPrevMonth} disabled={!canNavigatePrev} accessibilityLabel={previousMonthLabel} accessibilityRole="button">
        <View style={styles.navButtonWrap}>
          <View style={[styles.navButton, !canNavigatePrev && styles.navButtonDisabled]}>
            <SvgIcon name="arrow-back" size={24} color={canNavigatePrev ? active : disabled} />
          </View>
        </View>
      </Clickable>
      <Text style={month_title_style} numberOfLines={1}>
        {formatDisplayMonthYear(currentMonth)}
      </Text>
      <Clickable onPress={onNextMonth} disabled={!canNavigateNext} accessibilityLabel={nextMonthLabel} accessibilityRole="button">
        <View style={styles.navButtonWrap}>
          <View style={[styles.navButton, !canNavigateNext && styles.navButtonDisabled]}>
            <SvgIcon name="arrow-forward" size={24} color={canNavigateNext ? active : disabled} />
          </View>
        </View>
      </Clickable>
    </View>
  );
}

function create_month_row_style(theme_c: ThemeColors) {
  return {
    ...horizontalLtrDirectionStyle,
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    justifyContent: 'space-between' as const,
    backgroundColor: theme_c.background,
    borderRadius: appTheme.borderRadius.md,
    paddingVertical: appTheme.spacing.xs,
    paddingHorizontal: appTheme.spacing.xs,
  };
}

const styles = StyleSheet.create({
  navButtonWrap: {},
  navButton: {
    padding: appTheme.spacing.sm,
  },
  navButtonDisabled: {
    opacity: 0.4,
  },
  monthTitle: {
    ...appTheme.typography.bodyMedium,
    flex: 1,
    textAlign: 'center',
  },
});
